import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { dealerPortalLabels, getCountryDisplayName } from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDealerPortal } from "@/hooks/useDealerPortal";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { DealerPortalCompany } from "@/types/dealerPortal";
import {
  Building2,
  Mail,
  MapPin,
  Phone,
  User,
  ImagePlus,
  Loader2,
  Trash2,
} from "lucide-react";
import { MediaService } from "@/services/media/mediaService";
import { toast } from "sonner";
import {
  compressImageForPreview,
  compressImageForFullSize,
} from "@/utils/imageCompression";
import { FileUploadTrigger } from "@/components/native/FileUploadTrigger";
import { NativeImage } from "@/components/native/NativeImage";

interface DealerPortalCompanyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: DealerPortalCompany;
  companyLogoUrl?: string | null;
}

export function DealerPortalCompanyDialog({
  open,
  onOpenChange,
  company,
  companyLogoUrl,
}: DealerPortalCompanyDialogProps) {
  const { language } = useLanguage();
  const cd = dealerPortalLabels(language).companyDialog as Record<
    string,
    string
  >;
  const { user: currentUser } = useAuth();
  const { colleagues, updateCompanyLogo } = useDealerPortal();
  const colleaguesData = colleagues.data ?? [];
  const colleaguesLoading = colleagues.isLoading;
  const [logoUploading, setLogoUploading] = useState(false);
  const [fullSizeOpen, setFullSizeOpen] = useState(false);
  const [fullSizeUrl, setFullSizeUrl] = useState<string | null>(null);
  const [fullSizeLoading, setFullSizeLoading] = useState(false);
  const hasLogo = !!(company.logoPreviewMediaId ?? companyLogoUrl);

  const handleViewFullSize = async () => {
    if (!hasLogo) return;
    if (company.logoFullSizeMediaId) {
      setFullSizeOpen(true);
      setFullSizeLoading(true);
      setFullSizeUrl(null);
      try {
        const url = await MediaService.getDownloadUrl(
          company.logoFullSizeMediaId,
          3600,
        );
        setFullSizeUrl(url);
      } catch {
        toast.error(cd.toastImageError ?? "Kunde inte ladda bilden.");
        setFullSizeOpen(false);
      } finally {
        setFullSizeLoading(false);
      }
    } else if (companyLogoUrl) {
      setFullSizeUrl(companyLogoUrl);
      setFullSizeOpen(true);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) {
      toast.error(cd.toastSelectImage ?? "Välj en bildfil.");
      return;
    }
    setLogoUploading(true);
    try {
      // Use central image compression utilities (same as News, Campaign, CarTest, etc.)
      const previewFile = await compressImageForPreview(file);
      const fullSizeFile = await compressImageForFullSize(file);

      const meta = (f: File) => ({
        originalName: f.name,
        mimeType: f.type,
        sizeBytes: f.size,
      });

      const [previewPresigned, fullPresigned] = await Promise.all([
        MediaService.requestPresignedUploadUrl(
          "companyLogo",
          company.id,
          meta(previewFile),
          "preview",
        ),
        MediaService.requestPresignedUploadUrl(
          "companyLogo",
          company.id,
          meta(fullSizeFile),
          "fullsize",
        ),
      ]);

      await Promise.all([
        MediaService.uploadFileToS3(previewPresigned.uploadUrl, previewFile),
        MediaService.uploadFileToS3(fullPresigned.uploadUrl, fullSizeFile),
      ]);

      const [previewMedia, fullMedia] = await Promise.all([
        MediaService.createMediaAfterUpload(
          previewPresigned.s3Key,
          meta(previewFile),
          {
            companyId: company.id,
            mediaSize: "PREVIEW",
          },
        ),
        MediaService.createMediaAfterUpload(
          fullPresigned.s3Key,
          meta(fullSizeFile),
          {
            companyId: company.id,
            mediaSize: "FULL_SIZE",
          },
        ),
      ]);

      await Promise.all([
        MediaService.updateMediaStatus(previewMedia.id, "UPLOADED"),
        MediaService.updateMediaStatus(fullMedia.id, "UPLOADED"),
      ]);

      await updateCompanyLogo.mutateAsync({
        logoPreviewMediaId: previewMedia.id,
        logoFullSizeMediaId: fullMedia.id,
      });
      toast.success(cd.toastLogoUploaded ?? "Företagslogotyp uppladdad.");
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : (cd.toastLogoError ?? "Kunde inte ladda upp logga.");
      toast.error(msg);
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await updateCompanyLogo.mutateAsync({
        logoPreviewMediaId: null,
        logoFullSizeMediaId: null,
      });
      toast.success(cd.toastLogoRemoved ?? "Logotyp borttagen.");
    } catch {
      toast.error(cd.toastRemoveError ?? "Kunde inte ta bort logotyp.");
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md overflow-x-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-500/50 dark:bg-gray-500/80 border border-border overflow-hidden text-emerald-600 dark:text-emerald-400">
                {companyLogoUrl ? (
                  <NativeImage
                    src={companyLogoUrl}
                    alt=""
                    className="h-full w-full object-contain rounded-lg"
                  />
                ) : (
                  <Building2 className="h-5 w-5" />
                )}
              </div>
              {cd.title ?? "Företagsinfo"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            {/* Företagslogotyp */}
            <div className="rounded-lg border p-4 space-y-3">
              <h3 className="text-sm font-medium text-foreground">
                {cd.logo ?? "Företagslogotyp"}
              </h3>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleViewFullSize}
                  disabled={!hasLogo}
                  className="flex h-14 w-14 bg-gray-500/50 dark:bg-gray-500/80 shrink-0 items-center justify-center rounded-xl border border-border overflow-hidden ring-offset-background transition focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:opacity-90"
                  title={cd.viewFullSize ?? "Visa i full storlek"}
                >
                  {companyLogoUrl ? (
                    <NativeImage
                      src={companyLogoUrl}
                      alt={cd.logo ?? "Företagslogotyp"}
                      className="h-full w-full object-contain rounded-lg p-0.5"
                    />
                  ) : (
                    <Building2 className="h-7 w-7 text-muted-foreground" />
                  )}
                </button>
                <div className="flex flex-wrap gap-2 min-w-0">
                  <FileUploadTrigger
                    accept="image/*"
                    disabled={logoUploading}
                    onFilesSelected={(files) => {
                      if (!files[0]) return;
                      const dt = new DataTransfer();
                      dt.items.add(files[0]);
                      void handleLogoUpload({
                        target: { files: dt.files },
                      } as React.ChangeEvent<HTMLInputElement>);
                    }}
                  >
                    {(open) => (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={open}
                        disabled={logoUploading}
                      >
                        {logoUploading ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ImagePlus className="h-4 w-4" />
                        )}
                        <span className="ml-1.5">
                          {logoUploading
                            ? (cd.uploading ?? "Laddar upp...")
                            : (cd.uploadLogo ?? "Ladda upp logga")}
                        </span>
                      </Button>
                    )}
                  </FileUploadTrigger>
                  {hasLogo && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleRemoveLogo}
                      disabled={updateCompanyLogo.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="ml-1.5">{cd.remove ?? "Ta bort"}</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
              <p className="font-semibold text-foreground">
                {company.name ||
                  (dealerPortalLabels(language).company ?? "Företag")}
              </p>
              {company.orgNumber && (
                <p className="text-sm text-muted-foreground">
                  {cd.orgNr ?? "Org.nr"} {company.orgNumber}
                </p>
              )}
              {company.vatNumber && (
                <p className="text-sm text-muted-foreground">
                  {cd.vatNr ?? "Momsnr."} {company.vatNumber}
                </p>
              )}
              {company.contactEmail && (
                <p className="text-sm flex items-center gap-1.5 text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <a
                    href={`mailto:${company.contactEmail}`}
                    className="text-foreground hover:underline truncate"
                  >
                    {company.contactEmail}
                  </a>
                </p>
              )}
              {company.contactPhone && (
                <p className="text-sm flex items-center gap-1.5 text-muted-foreground">
                  <Phone className="h-3.5 w-3.5 shrink-0" />
                  <a
                    href={`tel:${company.contactPhone}`}
                    className="text-foreground hover:underline"
                  >
                    {company.contactPhone}
                  </a>
                </p>
              )}
              {(company.street ||
                company.postalCode ||
                company.city ||
                company.country) && (
                <p className="text-sm flex items-start gap-1.5 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                  <span>
                    {[
                      company.street,
                      [company.postalCode, company.city]
                        .filter(Boolean)
                        .join(" "),
                      getCountryDisplayName(company.country, language),
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </p>
              )}
            </div>

            <div className="min-w-0 flex flex-col min-h-0 overflow-hidden">
              <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2 shrink-0">
                <User className="h-4 w-4" />
                {cd.colleagues ?? "Kollegor"}
              </h3>
              {colleaguesLoading ? (
                <p className="text-sm text-muted-foreground">
                  {dealerPortalLabels(language).loading ?? "Laddar..."}
                </p>
              ) : colleaguesData.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {cd.noColleagues ?? "Inga kollegor hittades."}
                </p>
              ) : (
                <ul className="space-y-2 overflow-y-auto overflow-x-hidden max-h-[min(50vh,320px)] pr-1 -mr-1 min-h-0">
                  {colleaguesData.map((col) => {
                    const u = col.user;
                    const name = u
                      ? [u.firstName, u.lastName].filter(Boolean).join(" ") ||
                        "—"
                      : "—";
                    const email = u?.email ?? "—";
                    const isCurrentUser =
                      currentUser?.id &&
                      u?.id &&
                      String(u.id) === String(currentUser.id);
                    return (
                      <li
                        key={col.id}
                        className="flex items-center gap-3 rounded-md border bg-background px-3 py-2 text-sm"
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                          <User className="h-4 w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-foreground truncate flex items-center gap-2 flex-wrap">
                            {name}
                            {isCurrentUser && (
                              <Badge
                                variant="secondary"
                                className="text-[10px] px-1.5 py-0 font-normal shrink-0"
                              >
                                {cd.you ?? "Du"}
                              </Badge>
                            )}
                          </p>
                          <p className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                            <Mail className="h-3 w-3 shrink-0" />
                            {email}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={fullSizeOpen} onOpenChange={setFullSizeOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto bg-gray-500/80 p-2 overflow-hidden flex items-center justify-center border-0">
          {fullSizeLoading ? (
            <div className="flex items-center justify-center min-h-[200px] text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : fullSizeUrl ? (
            <NativeImage
              src={fullSizeUrl}
              alt={cd.fullSizeAlt ?? "Företagslogotyp i full storlek"}
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
